import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import Badge from '@/components/Badge';
import { ZealResponse } from '@/services/workersV2API';

type Props = {
  zeal: ZealResponse;
};

const DetailRow = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <Stack
    style={{
      justifyContent: 'space-between',
      padding: '8px 0',
      borderBottom: '1px solid #F3F4F6',
    }}
  >
    <Small color="lighter">{label}</Small>
    <Body>{value}</Body>
  </Stack>
);

const formatDate = (dateString: string | null) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const ZealCard = ({ zeal }: Props) => {
  if (!zeal.available) {
    return (
      <Card title="Zeal Details">
        <Card.Section>
          <Stack vertical gap={8}>
            <Badge label="Not Available" status="neutral" />
            <Small color="lighter">
              {'reason' in zeal && zeal.reason
                ? zeal.reason
                : 'error' in zeal && zeal.error
                  ? zeal.error
                  : 'No Zeal data available for this worker'}
            </Small>
            {'employee_id' in zeal && zeal.employee_id && (
              <Small color="lighter">Employee ID: {zeal.employee_id}</Small>
            )}
          </Stack>
        </Card.Section>
      </Card>
    );
  }

  const { employee_id, taxable_location_id, work_location_id, tnc_date, data } =
    zeal;

  return (
    <Card title="Zeal Details">
      <Card.Section>
        <Stack vertical gap={8}>
          <Body weight="semibold">Employee Info</Body>
          <Stack vertical gap={4}>
            <DetailRow label="Employee ID" value={employee_id} />
            <DetailRow
              label="Taxable Location ID"
              value={taxable_location_id || 'N/A'}
            />
            <DetailRow
              label="Work Location ID"
              value={work_location_id || 'N/A'}
            />
            <DetailRow label="T&C Date" value={formatDate(tnc_date)} />
          </Stack>
        </Stack>
      </Card.Section>

      {data && Object.keys(data).length > 0 && (
        <Card.Section>
          <Stack vertical gap={8}>
            <Body weight="semibold">Additional Data</Body>
            <Stack vertical gap={4}>
              {Object.entries(data).map(([key, value]) => (
                <DetailRow
                  key={key}
                  label={key
                    .split('_')
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ')}
                  value={
                    typeof value === 'boolean' ? (
                      <Badge
                        label={value ? 'Yes' : 'No'}
                        status={value ? 'theme' : 'neutral'}
                      />
                    ) : typeof value === 'object' ? (
                      JSON.stringify(value)
                    ) : (
                      String(value)
                    )
                  }
                />
              ))}
            </Stack>
          </Stack>
        </Card.Section>
      )}
    </Card>
  );
};

export default ZealCard;
